import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, Calendar, Users, Mail, Phone, User, MessageSquare, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div data-section-id="48904" className="relative">
        <select className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none ${className}`} ref={ref} {...props}>
          {children}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"><path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
        </div>
      </div>;
});
Select.displayName = "Select";
export default function InquiryForEvents() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="plan-your-celebration" className="relative w-full py-24 overflow-hidden bg-background dark text-foreground font-sans">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1505473/img/cake_cutting_ceremony_soft.png)] bg-cover bg-center opacity-30 grayscale-[20%]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/85 to-background/95" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div ref={ref} className={`mx-auto max-w-2xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-10 text-center">
            <h2 className="text-4xl font-serif font-bold tracking-tight text-primary sm:text-5xl mb-4">
              Plan Your Celebration
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              From intimate private dining to grand bamboo hall celebrations, share your vision with us and let's create something extraordinary in Peliatan, Ubud.
            </p>
          </div>

          <div className="bg-card/60 backdrop-blur-xl border border-border/40 rounded-xl p-6 sm:p-10 shadow-xl">
            <FormProvider formKey="plan_celebration_form_bamboo_hall" formId="plan_celebration_form_bamboo_hall" sectionName="inquiry-for-events" defaultValues={{
            name: "",
            email: "",
            phone: "",
            event_type: "",
            event_date: "",
            guest_count: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            event_type: {
              required: true
            },
            event_date: {
              required: true
            },
            guest_count: {
              required: true,
              min: 1
            },
            message: {
              required: false
            }
          }} submitText="Send Inquiry" successMessage="Thank you for your interest in Ubud Event Space! We have received your inquiry and will reach out via WhatsApp or email shortly to discuss your vision." errorMessage="We couldn't send your inquiry at this time. Please check your details or contact us directly via WhatsApp.">
              <InquiryFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 rounded-full bg-secondary/20 flex items-center justify-center mb-2">
          <CircleCheck className="h-8 w-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/10">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
              <FormLabel className="text-foreground/90 font-medium">Full Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input placeholder="Enter your name" {...field} className="pl-9 bg-background/50 border-border/60 focus:ring-primary/30" />
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-secondary/70" />
                </div>
              </FormControl>
              <FormDescription className="sr-only">First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
              <FormLabel className="text-foreground/90 font-medium">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="email" placeholder="email@example.com" {...field} className="pl-9 bg-background/50 border-border/60 focus:ring-primary/30" />
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-secondary/70" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
              <FormLabel className="text-foreground/90 font-medium">WhatsApp Number <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="tel" placeholder="+62 ..." {...field} className="pl-9 bg-background/50 border-border/60 focus:ring-primary/30" />
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-secondary/70" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="event_type" rules={{
        required: "Please select an event type"
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
              <FormLabel className="text-foreground/90 font-medium">Event Category</FormLabel>
              <FormControl>
                <Select {...field} value={field.value || ""} className="bg-background/50 border-border/60 focus:ring-primary/30">
                  <option value="" disabled>Select an event type</option>
                  <option value="Wedding">Wedding & Engagement</option>
                  <option value="Private Dining">Private Dining</option>
                  <option value="Corporate">Corporate & Retreat</option>
                  <option value="Workshop">Workshop & Community</option>
                  <option value="Birthday">Birthday Celebration</option>
                  <option value="Other">Other Gathering</option>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="event_date" rules={{
        required: "Event date is required"
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
              <FormLabel className="text-foreground/90 font-medium">Preferred Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className="pl-9 bg-background/50 border-border/60 focus:ring-primary/30" />
                  <SafeHydrate><Calendar className="absolute left-3 top-2.5 h-4 w-4 text-secondary/70" /></SafeHydrate>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="guest_count" rules={{
        required: "Guest count is required",
        min: {
          value: 1,
          message: "Must be at least 1 guest"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-2 md:col-span-1">
              <FormLabel className="text-foreground/90 font-medium">Number of Guests</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="number" placeholder="e.g., 25" {...field} className="pl-9 bg-background/50 border-border/60 focus:ring-primary/30" min="1" />
                  <Users className="absolute left-3 top-2.5 h-4 w-4 text-secondary/70" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Event Vision & Details <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <div className="relative">
                <Textarea placeholder="Tell us about your theme, catering preferences, or any special requirements..." className="min-h-[120px] pl-9 pt-3 bg-background/50 border-border/60 focus:ring-primary/30" {...field} />
                <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-secondary/70" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium flex items-center gap-2">
            <span className="text-lg"></span> {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all rounded-lg shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Sending Inquiry...
          </> : "Submit Event Inquiry"}
      </Button>
    </Form>;
}